<template>
  <div class="participants h-100">
    <div class="participants__activity pt-1">
      <div class="participants__activity-wrapper">
        <div
          v-for="(message, i) in activityMessages"
          :key="i"
          class="participants__activity-message px-2 py-1"
        >
          <v-icon v-if="message.type === 'leave'" icon="logout"></v-icon>
          <v-icon v-else icon="login"></v-icon>

          {{ message.text }}
        </div>
      </div>
    </div>
    <div class="participants__presence pl-1">
      <h6>
        {{ $t("components.messageThread.participants.active") }} ({{ participantCount }})
      </h6>
      <div
        v-for="participant in activeParticipants"
        :key="participant.id"
        class="participants__participant d-flex align-items-center mb-1"
      >
        <subscriber-avatar class="mr-1" x-small :subscriber="participant" />

        {{ participant.name }}
      </div>

      <template v-if="inactiveParticipants.length">
        <h6 class="mt-4">{{ $t("components.messageThread.participants.inactive") }}</h6>
        <div
          v-for="participant in inactiveParticipants"
          :key="participant.id"
          class="participants__participant d-flex align-items-center mb-1"
        >
          <subscriber-avatar class="mr-1" x-small :subscriber="participant" />

          {{ participant.name }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from "vue"
import { mapState, mapGetters, mapMutations, mapActions } from "vuex"

import { subscribeToThread, unsubscribeFromThread } from "@/lib/pusher-utils"

import SubscriberAvatar from "@/components/SubscriberAvatar"

export default {
  components: {
    SubscriberAvatar
  },

  props: {
    messageThreadId: { type: [Number, String], required: true }
  },

  computed: {
    ...mapState("auth", ["user"]),
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters("messages", ["getMessageThread"]),

    messageThread() {
      return this.getMessageThread(this.messageThreadId)
    },

    activeParticipants() {
      return Object.values(this.participants).filter(p => p.active)
    },

    inactiveParticipants() {
      return Object.values(this.participants).filter(p => !p.active)
    }
  },

  data() {
    return {
      activityMessages: [], // { type: [join leave], text: String }
      participants: {}, // { id: { id: Number, name: String, initials: String, active: Boolean } }
      participantCount: 0,
      channel: null
    }
  },

  methods: {
    ...mapMutations("messages", ["pushMessageThread", "pushMessageToThread"]),
    ...mapActions("messages", ["fetchMessageThread", "fetchMessages"]),

    initializeSubscription() {
      this.fetchThread().then(() => {
        this.channel = subscribeToThread(this.messageThread.channel_id, {
          onMessage: () => {},
          onSubscribe: this.handleThreadSubscription,
          onMemberAdded: this.handleMemberAdded,
          onMemberRemoved: this.handleMemberRemoved
        })
      })
    },

    handleMemberAdded(e) {
      Vue.set(
        this.participants,
        e.id,
        this.buildParticipantObject(e.id, e.info)
      )

      this.participantCount = this.participantCount + 1
    },

    handleMemberRemoved(e) {
      if (this.participants[e.id]) this.participants[e.id].active = false

      this.participantCount = this.participantCount - 1
      this.activityMessages.push({ type: "leave", text: `${e.info.name} Left` })
    },

    handleThreadSubscription(e) {
      this.participantCount = e.count

      Object.keys(e.members).forEach(id => {
        Vue.set(
          this.participants,
          id,
          this.buildParticipantObject(id, e.members[id])
        )
      })
    },

    fetchThread() {
      this.isLoading = true

      return this.fetchMessageThread(this.messageThreadId).finally(() => {
        this.isLoading = false
      })
    },

    buildParticipantObject(id, info, active = true) {
      return {
        id,
        active,
        name: info.name,
        initials: info.initials
      }
    }
  },

  beforeDestroy() {
    unsubscribeFromThread(this.messageThread.channel_id)
  },

  watch: {
    isAuthenticated() {
      if (this.isAuthenticated) {
        this.initializeSubscription()
      }
    }
  },

  mounted() {
    if (this.isAuthenticated) {
      this.initializeSubscription()
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.participants {
  display: grid;
  grid-template-rows: 50% 50%;
}

.participants__activity {
  overflow: scroll;

  .participants__activity-wrapper {
    display: flex;
    flex-direction: column-reverse;
  }
}

.participants__presence {
  overflow: scroll;
  white-space: nowrap;
}

.participants__activity-message {
  white-space: nowrap;

  .v-icon {
    transform: scale(1.3);
    margin-right: $size--2;
    color: $color-ui--grey-60;
  }
}
</style>
